<template>
  <div class="modal fade" :id="id" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog">
      <form @submit.prevent="submitForm" class="form"> 
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{!edit ? $t('steps.create_step') : $t('steps.update_step')}}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
                  <span class="svg-icon svg-icon-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" 
                            fill="currentColor" />
                      <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                            fill="currentColor" />
                    </svg>
                  </span>
            </div>
          </div>
          <div class="modal-body">  
            <RequiredText />

            <div class="row mb-5">
              <div class="col-10 ">
                <Input
                  container-class="form-group"
                  :name="`name`+id"
                  type="text"
                  :label="$t('steps.title')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :isRequired="true" 
                  v-model="formData.title"
                />
              </div>
              <div class="col-2">
                <label class="form-label fs-6 text-black">
                  {{$t('steps.color')}} <span class="text-danger">*</span>
                </label>
                <div >
                  <ColorPicker 
                    v-model:pureColor="formData.color" 
                    format="hex"
                  />
                </div>
              </div>
            </div>
            <div class="row mb-8">
              <div class="col-12 col-md-12 col-lg-12">
                <div class="d-flex flex-column fv-row">
                  <label class="form-label fs-6 text-black mb-2">
                    {{ $t('taxes.percent') }} <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input type="number" v-model="formData.milestone_progress" @keypress="onlyNumber" step=".01" class="form-control form-control-solid"
                      required :placeholder="$t('taxes.percent_placeholder')" />
                    <span class="input-group-text" id="basic-addon2">%</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-check mb-8">
              <input class="form-check-input" @change.prevent="handleToggleDefault" type="checkbox" v-model="formData.default" id="flexCheckDefault"/>
              <label class="ms-2 form-label fs-6 text-black" for="flexCheckDefault">
                {{ $t('steps.status_default') }}
              </label>
            </div>
            <div class="row">
              <div class="col-12 col-md-12 col-lg-12">
                <Select2
                  :parent-id="'#'+id"
                  :is-required="true"
                  container-class="form-group"
                  :multiple="true"
                  :show-search="true"
                  :name="'status_'+id"
                  :label="$t('steps.choose_status')"
                  label-class="form-label fs-6 text-black"
                  v-model="formData.status"
              >
                <option v-for="status in getStatus" :value="status?.id" :key="status?.id">
                    {{ status.title }}
                </option>
              </Select2>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
            <Button btn-class="btn-sm me-3" type="submit" id="submit_btn" btn-text="button.save" size="7px 30px" :is-loading="isLoading"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, defineEmits, defineProps, watch, onMounted } from "vue";
  import Input from "@/components/Input.vue";
  import Button from "@/components/Button.vue";
  import RequiredText from "@/components/RequiredText.vue";
  import { useStore } from "vuex";
  import { ColorPicker } from "vue3-colorpicker";
  import "vue3-colorpicker/style.css";
  import { computed } from "vue";
  import Select2 from "@/components/Select2.vue";

  const emits = defineEmits(["load", 'stepCreated']); 

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    parent: {
      type: String,
      required: false,
    },
    form: {
      type: Object,
      default: () => ({
        id: "",
        title: "",
        color: "#000",
        status: [],
        milestone_progress: 0,
        default: false
      }),
    },
    edit: {
      type: Boolean,
      default: false,
    },
  });

  const store = useStore();
  const isLoading = ref(false);
  const formData = ref({ ...props.form });

  const currentSociety = computed(() => store.getters['offices/getSelectedCompany'])
  const getStatus = computed(() => store.getters['taskStatus/getTaskStatus'])

  watch(() => props.form, (newForm) => {
    formData.value = { ...newForm };
  }, { deep: true });


  onMounted(() => {
    store.dispatch("taskStatus/loadTaskStatus")
  })

  function handleToggleDefault (e) {
    if(e.target.checked) {
      getStatus.value.forEach((item) => {
        if(item.default){
          formData.value.status.push(item.id)
        }
      });
    }else{
      formData.value.status = []
    }
  }

  function onlyNumber($event) {
    let keyCode = ($event.keyCode ? $event.keyCode : $event.which);
    if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { 
      $event.preventDefault();
    }
  }

  function closeModal() {
    if(props.parent){
      $("#" + props.parent).modal("toggle");
    }
    $("#" + props.id).modal("toggle");
    formData.value.title = "";
    formData.value.color = "#000";
    formData.value.status = [];
    formData.value.milestone_progress = 0;
    formData.value.default = false;
  }

  function submitForm() {
    isLoading.value = true; 
    if (!props.edit) {
      store
        .dispatch("milestoneCategories/createTaskMilestone", {...formData.value, society_id: currentSociety?.value?.id})
        .then((resp) => {
          isLoading.value = false;
          emits("load");
          emits("stepCreated", resp?.data?.data?.id)
          closeModal();

          window.toastr["success"](i18n.global.t("notification.step_created"));
        })
        .catch((e) => {
          isLoading.value = false;
        });
    } else {
      store
        .dispatch("milestoneCategories/updateTaskMilestone", {...formData.value, society_id: currentSociety?.value?.id})
        .then(() => {
          isLoading.value = false;
          emits("load");
          closeModal();

          window.toastr["success"](i18n.global.t("notification.step_updated"));
        })
        .catch(() => {
          isLoading.value = false;
        });
    }
  }
</script>

<style scoped>
  .color-picker-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }
</style>
 